'use client';
import Breadcrumbs from '@/components/Breadcrumbs';
import LatestBlog from '@/components/LatestBlog';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import Script from 'next/script'; // Import Script from Next.js

const { publicRuntimeConfig } = getConfig() || {};
const website = publicRuntimeConfig?.website || process.env.NEXT_PUBLIC_WEBSITE;

const BlogPage = ({ blog }) => {
  if (!blog) {
    return <div>Loading...</div>;
  }

  const url = `${website}/blog/${blog.slug}`;

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Blogs', href: '/blog' },
  ];

  return (
    <>
      <Script
        src="https://platform-api.sharethis.com/js/sharethis.js#property=670d485ba6330b001a6017c9&product=inline-share-buttons&source=platform"
        strategy="afterInteractive"
        async="async"
      />
      <section className="blog max-w-screen-lg mx-auto py-10">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <h1 className="font-bold text-left mx-4 sm:mx-auto md:mx-0">
          {blog.title}
        </h1>
        <Image
          src={blog.featuredImage}
          alt={blog.title}
          width={1472}
          height={832}
          className="object-cover sm:rounded-xl mx-auto max-w-screen-xl w-full"
          priority
        />
        <div className="pt-4 flex justify-between items-between text-base md:text-lg">
          <div>
            {new Date(blog.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
          <div>
            <div className="mb-0">
              Written By <strong>{blog.author}</strong>
            </div>
          </div>
        </div>
        <div className="">
          <h3 className="text-2xl block text-left justify-start">
            {Array.isArray(blog.category) ? (
              blog.category.map((cat, index) => (
                <span key={index}>
                  <Link
                    href={`/blog/category/${encodeURIComponent(cat)}`}
                    className="uppercase text-base text-colorGrey-800 no-underline"
                  >
                    {cat}
                  </Link>
                  {index < blog.category.length - 1 && ', '}
                </span>
              ))
            ) : (
              <Link href={`/blog/category/Uncategorized`}>Uncategorized</Link>
            )}
          </h3>
        </div>

        <div>
          <div className="flex items-start justify-start space-x-6 py-2 mx-auto font-sans">
            <div className="sharethis-inline-share-buttons"></div>
          </div>
          <div className="blog-content mb-0 sm:mx-auto md:mx-0 p-0">
            <ReactMarkdown
              className="prose lg:prose-xl mx-auto prose-strong:font-bold prose-strong:text-blue-900"
              children={blog.content}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            />
            <div className="flex max-w-screen-lg mx-auto bg-colorRed-200 rounded-xl border-2 gap-4 border-colorRed-600 py-4 px-2 my-4">
              <div className="shrink-0 px-2">
                <Image
                  src={blog.authorphoto}
                  width={100}
                  height={100}
                  alt={blog.author}
                  className="rounded-full"
                />
              </div>
              <div className="flex-1">
                <div className="font-bold p-0 m-0 text-2xl">{blog.author}</div>
                <div className="text-lg p-0 m-0 pb-2">{blog.aboutauthor}</div>
              </div>
            </div>
          </div>
        </div>

        <LatestBlog />
      </section>
    </>
  );
};

BlogPage.propTypes = {
  blog: PropTypes.shape({
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    category: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]).isRequired,
    author: PropTypes.string.isRequired,
    authorphoto: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    featuredImage: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    aboutauthor: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(BlogPage);
